import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Flame, Send, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "@/lib/adminApi";
import SlaTimer from "@/components/admin/SlaTimer";
import StatusPill from "@/components/admin/StatusPill";
import { useAuth } from "@/lib/auth";

const PII_REGEX = /([a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,})|(\+?1?[\s\-.(]*\d{3}[\s\-.)]*\d{3}[\s\-.]*\d{4})/gi;

export default function AdminVipSplitScreen() {
    const { id } = useParams();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        try {
            const lead = await adminApi.lead(id);
            let sessionFull = null;
            if (lead?.lead?.session_id) {
                sessionFull = await adminApi.sessionFull(lead.lead.session_id);
            }
            setData({ ...lead, sessionFull });
        } catch (err) {
            toast.error("Couldn't load VIP view.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        const t = setInterval(load, 5000);
        return () => clearInterval(t);
         
    }, [id]);

    if (loading && !data) {
        return (
            <div className="flex items-center justify-center py-24">
                <Loader2 className="animate-spin text-mm-navy" />
            </div>
        );
    }

    const lead = data?.lead;
    const summary = data?.summary;
    const messages = data?.sessionFull?.messages || data?.messages || [];
    const alert = data?.sessionFull?.hot_alert;
    const carriers = data?.suggested_carriers || [];

    if (!lead) {
        return (
            <div className="mm-card p-8 text-center" data-testid="vip-missing">
                <p className="text-mm-muted">Lead not found.</p>
                <Link to="/admin/leads" className="mm-btn-outline mt-4">Back to leads</Link>
            </div>
        );
    }

    if (!lead.hot_flag) {
        return (
            <div className="mm-card p-8 text-center" data-testid="vip-not-hot">
                <ShieldAlert className="mx-auto mb-3 text-amber-600" />
                <p className="text-mm-muted">This lead isn't hot — the VIP view is reserved for hot leads.</p>
                <Link to={`/admin/leads?open=${lead.id}`} className="mm-btn-outline mt-4">Open lead drawer</Link>
            </div>
        );
    }

    const ack = async () => {
        if (!alert) return;
        await adminApi.ackAlert(alert.id);
        load();
    };
    const sendToBrent = async () => {
        if (!window.confirm("Send to Brent?")) return;
        try {
            await adminApi.patchLead(lead.id, { status: "Sent to Brent" });
            toast.success("Sent to Brent — handoff logged.");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't send.");
        }
    };

    return (
        <div className="space-y-4" data-testid="vip-split-screen">
            <div className="flex items-center justify-between">
                <button onClick={() => navigate(-1)} className="mm-btn-ghost">
                    <ArrowLeft size={14} /> Back
                </button>
                <div className="flex items-center gap-2">
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-red-200 bg-red-50 px-2.5 py-1 text-xs font-medium text-red-800">
                        <Flame size={12} /> HOT · {lead.score}/100
                    </span>
                    <SlaTimer
                        createdAt={alert?.created_at || lead.created_at}
                        acknowledgedAt={alert?.acknowledged_at}
                        size="lg"
                    />
                </div>
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
                {/* Left — transcript */}
                <section className="mm-card flex min-h-[620px] flex-col" data-testid="vip-transcript">
                    <header className="border-b border-mm-line px-5 py-3">
                        <div className="mm-eyebrow">Live transcript</div>
                        <div className="text-sm text-mm-muted">Polling every 5s · {messages.length} messages</div>
                    </header>
                    <div className="flex-1 overflow-y-auto px-5 py-4 space-y-2.5">
                        {messages.length === 0 ? (
                            <p className="text-sm text-mm-muted">No transcript attached to this lead.</p>
                        ) : (
                            messages.map((m) => (
                                <div
                                    key={m.id}
                                    className={`rounded-xl border px-3 py-2 text-sm ${
                                        m.role === "user"
                                            ? "border-mm-navy/10 bg-mm-navy/[0.03]"
                                            : "border-mm-line bg-white"
                                    }`}
                                >
                                    <div className="mm-eyebrow mb-0.5 !text-[9px]">
                                        {m.role === "user" ? "Prospect" : "Jarvis"}
                                    </div>
                                    <div
                                        className="whitespace-pre-wrap"
                                        dangerouslySetInnerHTML={{
                                            __html: m.content.replace(PII_REGEX, (s) => `<strong>${s}</strong>`),
                                        }}
                                    />
                                </div>
                            ))
                        )}
                    </div>
                </section>

                {/* Right — extracted + carriers + actions */}
                <section className="mm-card flex min-h-[620px] flex-col" data-testid="vip-sidepanel">
                    <header className="border-b border-mm-line px-5 py-3">
                        <div className="mm-eyebrow">VIP dossier</div>
                        <div className="mt-1 flex items-center gap-2">
                            <span className="mm-display text-xl text-mm-navy">{lead.name || "Unnamed"}</span>
                            <StatusPill status={lead.status} />
                        </div>
                    </header>
                    <div className="flex-1 space-y-5 overflow-y-auto px-5 py-4 text-sm">
                        <div data-testid="vip-extracted">
                            <div className="mm-eyebrow mb-2">Extracted fields</div>
                            <div className="grid grid-cols-2 gap-3">
                                <F label="Email">{lead.email || summary?.extracted?.email || "—"}</F>
                                <F label="Phone">{lead.phone || summary?.extracted?.phone || "—"}</F>
                                <F label="DOB">{lead.dob || summary?.extracted?.dob || "—"}</F>
                                <F label="Sex">{lead.sex || summary?.extracted?.sex || "—"}</F>
                                <F label="Tobacco">{(lead.tobacco ?? summary?.extracted?.tobacco) === true ? "Yes" : (lead.tobacco ?? summary?.extracted?.tobacco) === false ? "No" : "—"}</F>
                                <F label="Coverage">{lead.coverage_status || "—"}</F>
                                <F label="Product">{lead.product_interest || "—"}</F>
                                <F label="Budget">{lead.budget || "—"}</F>
                                <F label="Urgency">{lead.urgency || "—"}</F>
                                <F label="Score">{lead.score}/100</F>
                            </div>
                        </div>
                        <div data-testid="vip-carriers">
                            <div className="mm-eyebrow mb-2">Internal carrier ranking</div>
                            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-900">
                                Internal only · never shown to prospect
                            </div>
                            <ol className="mt-3 space-y-1.5">
                                {carriers.length === 0 ? (
                                    <li className="text-mm-muted">No ranking — product not classified.</li>
                                ) : (
                                    carriers.map((c, i) => (
                                        <li
                                            key={c}
                                            className="flex items-center justify-between rounded-lg border border-mm-line bg-white px-3 py-1.5"
                                        >
                                            <span>
                                                <span className="mr-2 tabular-nums text-mm-muted">{i + 1}</span>
                                                {c}
                                            </span>
                                        </li>
                                    ))
                                )}
                            </ol>
                        </div>
                        <div>
                            <div className="mm-eyebrow mb-2">Summary</div>
                            <p className="text-mm-ink leading-relaxed">
                                {summary?.summary_text || summary?.summary || "No Jarvis summary attached."}
                            </p>
                        </div>
                    </div>
                    <footer className="flex flex-col gap-2 border-t border-mm-line bg-mm-bg px-5 py-3 sm:flex-row">
                        {alert && !alert.acknowledged && (
                            <button onClick={ack} className="mm-btn-outline !py-2 flex-1" data-testid="vip-ack">
                                Acknowledge
                            </button>
                        )}
                        <Link to={`/admin/leads?open=${lead.id}`} className="mm-btn-outline !py-2 flex-1 text-center">
                            Open drawer
                        </Link>
                        {user?.role === "admin" && ["Approved", "Contact Ready", "Scheduled"].includes(lead.status) && (
                            <button onClick={sendToBrent} className="mm-btn-primary !py-2 flex-1" data-testid="vip-send-brent">
                                <Send size={14} /> Send to Brent
                            </button>
                        )}
                    </footer>
                </section>
            </div>
        </div>
    );
}

function F({ label, children }) {
    return (
        <div>
            <div className="text-[10px] font-medium uppercase tracking-wider text-mm-muted">
                {label}
            </div>
            <div className="text-mm-ink">{children}</div>
        </div>
    );
}
